import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, TrendingUp, TrendingDown, Scale } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import api, { formatApiError } from "../../lib/api";
import { money } from "./OverviewTab";

const inputCls =
  "w-full bg-obsidian border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-volt/60 transition-colors duration-300";

export default function AccountsTab() {
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ type: "income", title: "", category: "General", amount: "", note: "", date: new Date().toISOString().slice(0, 10) });

  const load = () => api.get("/accounts").then((r) => setEntries(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/accounts", { ...form, amount: parseFloat(form.amount) });
      toast.success("Entry recorded");
      setOpen(false);
      setForm({ type: "income", title: "", category: "General", amount: "", note: "", date: new Date().toISOString().slice(0, 10) });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/accounts/${id}`);
      toast.success("Entry deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const income = entries.filter((e) => e.type === "income").reduce((s, e) => s + e.amount, 0);
  const expense = entries.filter((e) => e.type === "expense").reduce((s, e) => s + e.amount, 0);

  const chartData = useMemo(() => {
    let run = 0;
    return [...entries]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((e) => {
        run += e.type === "income" ? e.amount : -e.amount;
        return { date: e.date.slice(5), balance: Math.round(run * 100) / 100 };
      });
  }, [entries]);

  const visible = entries.filter(
    (e) =>
      (filter === "all" || e.type === filter) &&
      (e.title.toLowerCase().includes(search.toLowerCase()) || e.category.toLowerCase().includes(search.toLowerCase()))
  );

  const summaryCards = [
    { icon: TrendingUp, label: "Total Income", value: money(income), cls: "text-green-400", testid: "accounts-income" },
    { icon: TrendingDown, label: "Total Expenses", value: money(expense), cls: "text-red-400", testid: "accounts-expense" },
    { icon: Scale, label: "Net Balance", value: money(income - expense), cls: "text-volt", testid: "accounts-net" },
  ];

  return (
    <div data-testid="accounts-tab" className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/5 border border-white/5">
        {summaryCards.map((c) => (
          <div key={c.label} className="bg-obsidian p-8">
            <c.icon size={20} strokeWidth={1.5} className={c.cls} />
            <p data-testid={c.testid} className={`mt-8 font-display font-extrabold tracking-tight text-3xl ${c.cls}`}>{c.value}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>

      {chartData.length > 1 && (
        <div className="border border-white/5 bg-panel/40 p-6" data-testid="accounts-chart">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Running Balance</p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
              <defs>
                <linearGradient id="voltFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#E6FF00" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#E6FF00" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={70} />
              <Tooltip contentStyle={{ background: "#12141D", border: "1px solid rgba(255,255,255,0.1)", fontFamily: "JetBrains Mono", fontSize: 12 }} />
              <Area type="monotone" dataKey="balance" stroke="#E6FF00" strokeWidth={2} fill="url(#voltFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2">
          {["all", "income", "expense"].map((f) => (
            <button
              key={f}
              data-testid={`accounts-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors duration-300 ${
                filter === f ? "border-volt/60 text-volt bg-volt/5" : "border-white/10 text-slate-500 hover:text-slate-200"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="flex gap-3 flex-1 sm:flex-none">
          <input data-testid="accounts-search-input" placeholder="Search entries..." value={search} onChange={(e) => setSearch(e.target.value)} className={`${inputCls} !py-2 flex-1 sm:w-56`} />
          <button data-testid="add-account-entry-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 bg-volt text-obsidian font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-5 py-2.5 hover:bg-[#CCEE00] transition-colors duration-300">
            <Plus size={14} /> Entry
          </button>
        </div>
      </div>

      <div className="border border-white/5 overflow-x-auto">
        <table className="w-full text-sm" data-testid="accounts-table">
          <thead>
            <tr className="border-b border-white/5 bg-panel/60">
              {["Date", "Title", "Category", "Type", "Amount", ""].map((h) => (
                <th key={h} className="text-left font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 px-5 py-4">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-500 text-sm">No entries yet. Record your first income or expense.</td></tr>
            ) : (
              visible.map((e) => (
                <tr key={e.id} data-testid={`account-entry-${e.id.slice(0, 8)}`} className="border-b border-white/5 hover:bg-panel/40 transition-colors duration-200">
                  <td className="px-5 py-4 font-mono text-xs text-slate-400">{e.date}</td>
                  <td className="px-5 py-4">{e.title}{e.note && <span className="block text-xs text-slate-500 mt-0.5">{e.note}</span>}</td>
                  <td className="px-5 py-4 font-mono text-xs text-slate-400">{e.category}</td>
                  <td className="px-5 py-4">
                    <span className={`font-mono text-[10px] uppercase tracking-[0.15em] border px-2.5 py-1 ${e.type === "income" ? "text-green-400 border-green-400/30 bg-green-400/10" : "text-red-400 border-red-400/30 bg-red-400/10"}`}>
                      {e.type}
                    </span>
                  </td>
                  <td className={`px-5 py-4 font-mono text-sm ${e.type === "income" ? "text-green-400" : "text-red-400"}`}>
                    {e.type === "income" ? "+" : "−"}{money(e.amount)}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button data-testid={`delete-entry-${e.id.slice(0, 8)}`} onClick={() => remove(e.id)} className="text-slate-600 hover:text-red-400 transition-colors duration-200" aria-label="Delete entry">
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-panel border-white/10 text-slate-50 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl">New Ledger Entry</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} data-testid="submit-account-entry-form" className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              {["income", "expense"].map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`entry-type-${t}`}
                  onClick={() => setForm({ ...form, type: t })}
                  className={`font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-3 border transition-colors duration-300 ${
                    form.type === t
                      ? t === "income" ? "border-green-400/60 text-green-400 bg-green-400/10" : "border-red-400/60 text-red-400 bg-red-400/10"
                      : "border-white/10 text-slate-500"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            <input data-testid="entry-title-input" required minLength={2} placeholder="Entry title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="entry-category-input" placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} />
              <input data-testid="entry-amount-input" required type="number" min="0.01" step="0.01" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls} />
            </div>
            <input data-testid="entry-date-input" required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={`${inputCls} [color-scheme:dark]`} />
            <input data-testid="entry-note-input" placeholder="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputCls} />
            <button data-testid="entry-submit-btn" disabled={busy} className="w-full bg-volt text-obsidian font-mono text-xs uppercase tracking-[0.2em] font-bold px-8 py-4 hover:bg-[#CCEE00] transition-colors duration-300 disabled:opacity-50">
              {busy ? "Recording..." : "Record Entry"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
